import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import { EMPTY_SPORT_FORM, SportDisciplineForm } from "../sports/SportDisciplineForm";
import { sportConflictCode, sportConflictMessage } from "../sports/sportConflict";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

/** I doppioni che il coach risolve seguendo lo sport che c'è già. */
const FOLLOWABLE = new Set(["exists_global", "exists_team", "exists_mine"]);

/**
 * Un coach aggiunge uno sport che non c'è (D1 di docs/sport-config).
 *
 * Nasce personale — lo vedono lui, i coach del suo team e i suoi atleti — e il
 * coach lo segue subito. Se il nome c'è già (anche scritto diverso: «Cross
 * Fit»), il server dice perché e la finestra propone la strada giusta:
 * seguire lo sport esistente, o chiedere all'admin di renderlo di tutti.
 *
 * Props: open, onOpenChange, onCreated(result), onFollow(value) (async).
 */
export function AddSportDialog({ open, onOpenChange, onCreated, onFollow }) {
	const { t } = useLanguage();
	const [form, setForm] = useState(EMPTY_SPORT_FORM);
	const [saving, setSaving] = useState(false);
	const [conflict, setConflict] = useState(null);

	useEffect(() => {
		if (open) {
			setForm(EMPTY_SPORT_FORM);
			setConflict(null);
		}
	}, [open]);

	const changeForm = (next) => {
		setForm(next);
		setConflict(null);
	};

	const create = async () => {
		const label = (form.label || "").trim();
		if (!label) return;
		setSaving(true);
		try {
			const result = await fetchAPI("/sports", {
				method: "POST",
				body: JSON.stringify({ label }),
			});
			const name = result?.sport?.label || label;
			toast.success(
				(result?.reactivated ? t("scSportReactivated") : t("scSportCreated")).replace(
					"{label}",
					name,
				),
			);
			onOpenChange(false);
			await onCreated?.(result);
		} catch (error) {
			if (error?.status === 409) {
				setConflict({
					code: sportConflictCode(error),
					sport: error?.detail?.sport || null,
					message: sportConflictMessage(error, t, "coach"),
				});
			} else {
				toast.error(sportConflictMessage(error, t, "coach"));
			}
		} finally {
			setSaving(false);
		}
	};

	const follow = async () => {
		if (!conflict?.sport?.value) return;
		setSaving(true);
		try {
			await onFollow?.(conflict.sport.value);
			onOpenChange(false);
		} finally {
			setSaving(false);
		}
	};

	const canFollow = conflict && FOLLOWABLE.has(conflict.code) && conflict.sport?.value;

	return (
		<Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
			<DialogContent className="bg-card border-border max-w-md">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle>{t("scAddSportTitle")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 text-sm">
					<SportDisciplineForm
						value={form}
						onChange={changeForm}
						mode="create"
						variant="coach"
						disabled={saving}
					/>
					{conflict && (
						<div
							className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-3 space-y-2 text-amber-300"
							data-testid="sport-conflict"
						>
							<p>{conflict.message}</p>
							{canFollow && (
								<Button
									size="sm"
									variant="outline"
									className="rounded-sm"
									disabled={saving}
									onClick={follow}
								>
									{t("scFollowExisting").replace(
										"{label}",
										conflict.sport.label || conflict.sport.value,
									)}
								</Button>
							)}
						</div>
					)}
					<div className="flex justify-end gap-2 pt-1">
						<Button
							variant="ghost"
							className="rounded-sm"
							disabled={saving}
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							disabled={saving || !(form.label || "").trim()}
							onClick={create}
						>
							{saving ? (
								<Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
							) : (
								<Plus className="w-4 h-4 mr-1.5" />
							)}
							{t("scAddSportConfirm")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
